import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard } from 'react-native';
import {
    ControlledInput,
    ControlledInputTrigger,
    ControlledMultilineInput,
    Form,
    USE_FORM_MODE_DEFAULTS,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useFlow } from '../../common/providers/FlowProvider';
import { useFlowRouter } from '../../common/providers/FlowRouter';
import { BaseStep } from '../../common/Step';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
    formattedDollarsToCents,
} from '../../common/util/currencyUtils';
import type { AddIncomeFlowSteps } from '../AddIncomeFlow';
import IncomePayCycleSelectionSheet from '../sheets/IncomePayCycleSelectionSheet';
import { type Income, type IncomePayCycle } from '../types/Income';
import { formatPayCycle } from '../utils/incomeUtils';

const freelanceIncomeDetailsSchema = z.object({
    monthlyTakeHome: z.string().min(3), // monthlyTakeHome is the formatted string, which includes a left padding of two characters: "$ ".
    payCycle: z.string().min(1, 'Required field'),
    additionalInformation: z.string().optional(),
});

type FreelanceIncomeDetailsSchema = z.infer<
    typeof freelanceIncomeDetailsSchema
>;

export const FreelanceDetailsStep = () => {
    const { data } = useFlow<Income>();
    const { onContinue } = useFlowRouter<Income, AddIncomeFlowSteps>();

    const [isPayCycleSheetOpen, setIsPayCycleSheetOpen] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
        trigger,
        watch,
    } = useForm<FreelanceIncomeDetailsSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(freelanceIncomeDetailsSchema),
        defaultValues: {
            monthlyTakeHome: centsToFormattedDollars(data.monthlyTakeHomeCents),
            payCycle: data.payCycle ?? '',
            additionalInformation: data.additionalInformation ?? '',
        },
    });

    const onSubmit: SubmitHandler<FreelanceIncomeDetailsSchema> = async ({
        monthlyTakeHome,
        payCycle,
        additionalInformation,
    }: FreelanceIncomeDetailsSchema) => {
        const incomeData = {
            monthlyTakeHomeCents: formattedDollarsToCents(monthlyTakeHome),
            payCycle: payCycle as IncomePayCycle,
            additionalInformation,
        };

        await onContinue(incomeData);
    };

    const payCycle = watch('payCycle');

    return (
        <BaseStep
            onContinue={handleSubmit(onSubmit)}
            isContinueDisabled={!isValid}
        >
            <YStack gap={12}>
                <Form onSubmit={handleSubmit(onSubmit)} marginBottom={12}>
                    <YStack gap={12}>
                        <ControlledInput
                            label="Monthly take-home (approximate)"
                            name="monthlyTakeHome"
                            type="numeric"
                            inputMode="numeric"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="next"
                            formatValueOnChange={formatCurrencyInputOnChange}
                        />
                        <ControlledInputTrigger
                            label="Pay cycle"
                            name="payCycle"
                            formatValue={formatPayCycle}
                            control={control}
                            register={register}
                            errors={errors}
                            onTrigger={() => {
                                setIsPayCycleSheetOpen(true);
                            }}
                        />
                        <ControlledMultilineInput
                            label="Additional information (optional)"
                            name="additionalInformation"
                            control={control}
                            register={register}
                            errors={errors}
                            enterKeyHint="done"
                            blurOnSubmit={true}
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                        />
                    </YStack>
                </Form>
            </YStack>
            <IncomePayCycleSelectionSheet
                isOpen={isPayCycleSheetOpen}
                selectedOption={payCycle as IncomePayCycle}
                onSelectOption={(payCycle: IncomePayCycle | null) => {
                    if (payCycle != null) {
                        setValue('payCycle', payCycle);
                        void trigger('payCycle'); // Manually trigger form validation after setValue
                    }
                }}
                onClose={() => {
                    setIsPayCycleSheetOpen(false);
                }}
            />
        </BaseStep>
    );
};
